// src/pages/hr/learning/LearningOverviewTab.jsx
// ============================================================
// Learning & Development - Overview Tab
// Redesigned with Green Theme & Outline Icons
// ============================================================

import React, { useMemo } from "react";
import {
  BookOpen,
  CheckCircle,
  Clock,
  Award,
  Users,
  Building,
  ArrowRight,
  FileCheck,
  Calendar,
  BookX,
  BarChart3,
  ClipboardX,
  AlarmClock,
} from "lucide-react";

export default function LearningOverviewTab({
  learningState,
  departments = [],
  currentUser,
  onNavigateTab,
  onSelectCourse,
}) {
  const { data } = learningState;
  const courses = data?.courses || [];
  const progressRecords = data?.progressRecords || [];

  // ── KPI metrics from real data only ────────────────────────────────────────
  const totalCourses = courses.length;
  const assignedCount = progressRecords.length;
  const completedCount = progressRecords.filter(
    (p) => p.status === "completed" || (p.quizAttempted && p.passed)
  ).length;
  const pendingCount = progressRecords.filter(
    (p) => p.status === "in_progress" || (!p.quizAttempted && p.status !== "completed")
  ).length;

  const passedQuizzes = progressRecords.filter((p) => p.passed).length;
  const attemptedQuizzes = progressRecords.filter((p) => p.quizAttempted).length;
  const quizPassRate =
    attemptedQuizzes > 0
      ? Math.round((passedQuizzes / attemptedQuizzes) * 100)
      : null;

  // ── Department progress from real progress records ─────────────────────────
  const deptProgress = useMemo(() => {
    if (progressRecords.length === 0) return [];

    const deptMap = {};
    progressRecords.forEach((rec) => {
      const dept = rec.department || rec.employeeDept || "Unknown";
      if (!deptMap[dept]) deptMap[dept] = { total: 0, completed: 0 };
      deptMap[dept].total++;
      if (rec.status === "completed" || (rec.quizAttempted && rec.passed)) {
        deptMap[dept].completed++;
      }
    });

    return Object.entries(deptMap).map(([dept, counts]) => ({
      dept,
      pct: counts.total > 0 ? Math.round((counts.completed / counts.total) * 100) : 0,
    }));
  }, [progressRecords]);

  // ── Recent quiz results from real progress records ─────────────────────────
  const recentQuizResults = useMemo(() => {
    return progressRecords
      .filter((p) => p.quizAttempted)
      .sort((a, b) => {
        const ta = a.quizAttemptedAt?.toMillis?.() || 0;
        const tb = b.quizAttemptedAt?.toMillis?.() || 0;
        return tb - ta;
      })
      .slice(0, 5);
  }, [progressRecords]);

  // ── Upcoming deadlines from real courses ──────────────────────────────────
  const upcomingDeadlines = useMemo(() => {
    const today = new Date();
    return courses
      .filter((c) => c.deadline)
      .map((c) => ({
        ...c,
        deadlineDate: new Date(c.deadline),
        daysLeft: Math.ceil((new Date(c.deadline) - today) / (1000 * 60 * 60 * 24)),
      }))
      .filter((c) => c.daysLeft >= 0)
      .sort((a, b) => a.daysLeft - b.daysLeft)
      .slice(0, 5);
  }, [courses]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <style>{`
        .lnd-kpi-card {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          padding: 1.15rem 1.25rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.03);
          transition: all 0.2s ease;
        }
        .lnd-kpi-card:hover {
          border-color: #10b981;
          box-shadow: 0 4px 14px rgba(16, 185, 129, 0.08);
        }
        .lnd-kpi-icon {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          background: #f0fdf4;
          border: 1.5px solid #bbf7d0;
          color: #15803d;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .lnd-green-badge {
          background: #f0fdf4;
          color: #15803d;
          border: 1.5px solid #bbf7d0;
          padding: 0.2rem 0.6rem;
          border-radius: 99px;
          font-size: 0.72rem;
          font-weight: 700;
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
        }
      `}</style>

      {/* ── 5 KPI CARDS - ALL UNIFIED GREEN OUTLINE THEME ───────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "1rem" }}>
        {/* Total Courses */}
        <div className="lnd-kpi-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
            <span style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 700 }}>Total Courses</span>
            <div className="lnd-kpi-icon">
              <BookOpen size={18} strokeWidth={1.8} />
            </div>
          </div>
          <div style={{ fontSize: "1.65rem", fontWeight: 800, color: "#0f172a" }}>
            {totalCourses}
          </div>
          <div style={{ fontSize: "0.75rem", color: "#15803d", marginTop: "4px", fontWeight: 600 }}>
            {totalCourses === 0 ? "No courses added yet" : "Active Training Modules"}
          </div>
        </div>

        {/* Assigned Courses */}
        <div className="lnd-kpi-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
            <span style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 700 }}>Assigned Courses</span>
            <div className="lnd-kpi-icon">
              <Users size={18} strokeWidth={1.8} />
            </div>
          </div>
          <div style={{ fontSize: "1.65rem", fontWeight: 800, color: "#0f172a" }}>
            {assignedCount}
          </div>
          <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "4px", fontWeight: 500 }}>
            {assignedCount === 0 ? "No assignments yet" : "Across All Departments"}
          </div>
        </div>

        {/* Completed Courses */}
        <div className="lnd-kpi-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
            <span style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 700 }}>Completed</span>
            <div className="lnd-kpi-icon">
              <CheckCircle size={18} strokeWidth={1.8} />
            </div>
          </div>
          <div style={{ fontSize: "1.65rem", fontWeight: 800, color: "#15803d" }}>
            {completedCount}
          </div>
          <div style={{ fontSize: "0.75rem", color: "#15803d", marginTop: "4px", fontWeight: 600 }}>
            {completedCount === 0 ? "No completions yet" : "Videos & Quizzes Finished"}
          </div>
        </div>

        {/* Pending Courses */}
        <div className="lnd-kpi-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
            <span style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 700 }}>Pending</span>
            <div className="lnd-kpi-icon">
              <Clock size={18} strokeWidth={1.8} />
            </div>
          </div>
          <div style={{ fontSize: "1.65rem", fontWeight: 800, color: "#0f172a" }}>
            {pendingCount}
          </div>
          <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "4px", fontWeight: 500 }}>
            {pendingCount === 0 ? "Nothing pending" : "Awaiting Completion"}
          </div>
        </div>

        {/* Quiz Pass Rate */}
        <div className="lnd-kpi-card">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.6rem" }}>
            <span style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 700 }}>Quiz Pass Rate</span>
            <div className="lnd-kpi-icon">
              <Award size={18} strokeWidth={1.8} />
            </div>
          </div>
          <div style={{ fontSize: "1.65rem", fontWeight: 800, color: "#0f172a" }}>
            {quizPassRate !== null ? `${quizPassRate}%` : "—"}
          </div>
          <div style={{ fontSize: "0.75rem", color: "#15803d", marginTop: "4px", fontWeight: 600 }}>
            {quizPassRate === null ? "No quizzes attempted yet" : "Min 70% Pass Score"}
          </div>
        </div>
      </div>

      {/* ── DASHBOARD SECTIONS GRID ──────────────────────────────────────────── */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: "1.5rem" }}>

        {/* Left Column */}
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>

          {/* Department Learning Progress */}
          <div className="card" style={{ padding: "1.35rem", background: "#ffffff", borderRadius: "14px", border: "1px solid #e2e8f0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, display: "flex", alignItems: "center", gap: "0.55rem", color: "#0f172a" }}>
                <Building size={18} strokeWidth={1.8} color="#15803d" /> Department Learning Progress
              </h3>
              <button
                onClick={() => onNavigateTab("courses")}
                style={{ background: "none", border: "none", color: "#15803d", fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}
              >
                Catalog <ArrowRight size={14} strokeWidth={2} />
              </button>
            </div>

            {deptProgress.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2.5rem 1rem", color: "#64748b", gap: "0.5rem" }}>
                <BarChart3 size={32} strokeWidth={1.5} style={{ opacity: 0.35 }} />
                <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155" }}>No department progress available</span>
                <span style={{ fontSize: "0.75rem" }}>Progress will appear once employees are assigned courses.</span>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                {deptProgress.map(({ dept, pct }, idx) => (
                  <div key={idx}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.82rem", marginBottom: "0.35rem", fontWeight: 600, color: "#334155" }}>
                      <span>{dept}</span>
                      <span style={{ fontWeight: 800, color: "#15803d" }}>{pct}%</span>
                    </div>
                    <div style={{ width: "100%", height: "8px", background: "#e2e8f0", borderRadius: "99px", overflow: "hidden" }}>
                      <div
                        style={{
                          width: `${pct}%`,
                          height: "100%",
                          background: "#10b981",
                          borderRadius: "99px",
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent Assigned Courses Table */}
          <div className="card" style={{ padding: "1.35rem", background: "#ffffff", borderRadius: "14px", border: "1px solid #e2e8f0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 800, display: "flex", alignItems: "center", gap: "0.55rem", color: "#0f172a" }}>
                <BookOpen size={18} strokeWidth={1.8} color="#15803d" /> Recent Courses
              </h3>
              <button
                onClick={() => onNavigateTab("courses")}
                style={{ background: "none", border: "none", color: "#15803d", fontWeight: 700, fontSize: "0.82rem", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}
              >
                View All <ArrowRight size={14} strokeWidth={2} />
              </button>
            </div>

            {courses.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2.5rem 1rem", color: "#64748b", gap: "0.5rem" }}>
                <BookX size={32} strokeWidth={1.5} style={{ opacity: 0.35 }} />
                <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155" }}>No courses available</span>
                <span style={{ fontSize: "0.75rem" }}>Create a course in the Courses tab to get started.</span>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="table" style={{ width: "100%", fontSize: "0.83rem", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "0.6rem 0.75rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Course Name</th>
                      <th style={{ textAlign: "left", padding: "0.6rem 0.75rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Category</th>
                      <th style={{ textAlign: "left", padding: "0.6rem 0.75rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Duration</th>
                      <th style={{ textAlign: "left", padding: "0.6rem 0.75rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Assigned By</th>
                      <th style={{ textAlign: "left", padding: "0.6rem 0.75rem", background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0", fontSize: "0.75rem", fontWeight: 700, color: "#475569" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courses.slice(0, 5).map((crs, idx) => (
                      <tr
                        key={crs.id || idx}
                        onClick={() => onSelectCourse && onSelectCourse(crs)}
                        style={{ cursor: "pointer", borderBottom: "1px solid #f1f5f9" }}
                        className="table-row-hover"
                      >
                        <td style={{ padding: "0.65rem 0.75rem", fontWeight: 700, color: "#0f172a" }}>{crs.title}</td>
                        <td style={{ padding: "0.65rem 0.75rem" }}>
                          <span className="lnd-green-badge">{crs.category || "General"}</span>
                        </td>
                        <td style={{ padding: "0.65rem 0.75rem", color: "#475569" }}>{crs.duration || "—"}</td>
                        <td style={{ padding: "0.65rem 0.75rem", color: "#475569" }}>{crs.assignedBy || crs.createdBy || "HR Admin"}</td>
                        <td style={{ padding: "0.65rem 0.75rem" }}>
                          <span className="lnd-green-badge">
                            {crs.status === "archived" ? "Archived" : "Active"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Quiz Results & Deadlines */}
        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>

          {/* Recent Quiz Results */}
          <div className="card" style={{ padding: "1.35rem", background: "#ffffff", borderRadius: "14px", border: "1px solid #e2e8f0" }}>
            <h3 style={{ margin: "0 0 1rem 0", fontSize: "1rem", fontWeight: 800, display: "flex", alignItems: "center", gap: "0.55rem", color: "#0f172a" }}>
              <FileCheck size={18} strokeWidth={1.8} color="#15803d" /> Recent Quiz Results
            </h3>

            {recentQuizResults.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem 0.5rem", color: "#64748b", gap: "0.5rem" }}>
                <ClipboardX size={28} strokeWidth={1.5} style={{ opacity: 0.35 }} />
                <span style={{ fontSize: "0.84rem", fontWeight: 700, color: "#334155" }}>No quiz results available</span>
                <span style={{ fontSize: "0.75rem", textAlign: "center" }}>Results appear after employees attempt quizzes.</span>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                {recentQuizResults.map((rec, idx) => {
                  const score = rec.score ?? rec.quizScore ?? null;
                  const passed = rec.passed;
                  return (
                    <div
                      key={rec.id || idx}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "0.65rem 0.85rem",
                        borderRadius: "10px",
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: "0.84rem", fontWeight: 800, color: "#0f172a" }}>
                          {rec.employeeName || rec.userName || "Employee"}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#64748b", fontWeight: 500 }}>
                          {rec.courseTitle || rec.courseName || "Course"}
                        </div>
                      </div>
                      <div className="lnd-green-badge">
                        {score !== null ? `${score}%` : "—"} ({passed ? "PASS" : "FAIL"})
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Upcoming Learning Deadlines */}
          <div className="card" style={{ padding: "1.35rem", background: "#ffffff", borderRadius: "14px", border: "1px solid #e2e8f0" }}>
            <h3 style={{ margin: "0 0 1rem 0", fontSize: "1rem", fontWeight: 800, display: "flex", alignItems: "center", gap: "0.55rem", color: "#0f172a" }}>
              <Calendar size={18} strokeWidth={1.8} color="#15803d" /> Upcoming Deadlines
            </h3>

            {upcomingDeadlines.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "2rem 0.5rem", color: "#64748b", gap: "0.5rem" }}>
                <AlarmClock size={28} strokeWidth={1.5} style={{ opacity: 0.35 }} />
                <span style={{ fontSize: "0.84rem", fontWeight: 700, color: "#334155" }}>No upcoming deadlines</span>
                <span style={{ fontSize: "0.75rem", textAlign: "center" }}>Set a deadline on a course for it to appear here.</span>
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", fontSize: "0.82rem" }}>
                {upcomingDeadlines.map((crs, idx) => {
                  return (
                    <div
                      key={crs.id || idx}
                      style={{
                        padding: "0.8rem",
                        background: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                        borderLeft: "4px solid #10b981",
                        borderRadius: "10px",
                      }}
                    >
                      <div style={{ fontWeight: 800, color: "#143724" }}>
                        {crs.title}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#15803d", marginTop: "3px", fontWeight: 600 }}>
                        Deadline: {crs.deadline} ({crs.daysLeft} day{crs.daysLeft !== 1 ? "s" : ""} left)
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
